<!--
  The honest table (refresh_plan.md 1.2 / phase 1 centrepiece).

  The point of this component is the state triad: today all fifteen G2 list
  pages (audit_web.md 3.6-3.9) render the same header-row-over-an-empty-box
  for an empty table, a loading table and a failed fetch, because `cTable`
  has no `loading` prop and none of the fifteen call sites has a `.catch`.
  `loading` and `error` are required props here so a caller cannot silently
  omit the failure state the way every existing list page did.

  Precedence is error, then loading, then empty, then rows (contract): a
  failed fetch has zero rows, and showing "no loads yet" instead of the
  error is the specific lie this component exists to stop.
-->
<template>
  <div class="lw-c-table" :class="densityClass">
    <table class="lw-c-table__table" :aria-busy="ariaBusy">
      <caption
        class="lw-c-table__caption"
        :class="{ 'lw-c-table__caption--visible': captionVisible }"
      >
        {{
          caption
        }}
      </caption>
      <thead>
        <tr class="lw-c-table__header-row">
          <th
            v-for="col in columns"
            :key="col.key"
            scope="col"
            class="lw-c-table__header-cell"
            :class="headerCellClasses(col)"
            :style="colStyle(col)"
            :aria-sort="ariaSortFor(col)"
          >
            <!--
              Hard rule: a sortable header is a real button, a non-sortable
              header is plain text, and the difference has to be visible
              before hover (audit_web.md 3.9: "no visual difference between
              a sortable and a non-sortable header until hover" on Dispatch).
            -->
            <button
              v-if="col.sortable"
              type="button"
              class="lw-c-table__sort-btn"
              @click="handleSort(col)"
            >
              <span class="lw-c-table__sort-label">{{ col.label }}</span>
              <span
                class="lw-c-table__sort-icon"
                :class="sortIconClasses(col)"
                aria-hidden="true"
              />
            </button>
            <span v-else class="lw-c-table__header-label">{{ col.label }}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="displayMode === 'error'" class="lw-c-table__state-row">
          <td :colspan="colCount" class="lw-c-table__state-cell">
            <div role="alert" class="lw-c-table__error">
              <p class="lw-c-table__error-message">
                {{ errorMessage }}
              </p>
              <LwButton variant="secondary" size="sm" @click="$emit('retry')"> Retry </LwButton>
            </div>
          </td>
        </tr>

        <template v-if="displayMode === 'loading'">
          <!--
            Three skeleton rows inside the real <tbody>, at the table's own
            density, so the layout does not jump once rows arrive
            (contract). Cells are aria-hidden; the accessible announcement
            is the live region below the table, not this markup.
          -->
          <tr v-for="n in 3" :key="'lw-table-skeleton-' + n" class="lw-c-table__skeleton-row">
            <td
              v-for="col in columns"
              :key="col.key"
              class="lw-c-table__cell lw-c-table__skeleton-cell"
              aria-hidden="true"
            >
              <span class="lw-c-table__skeleton-bar" />
            </td>
          </tr>
        </template>

        <tr v-if="displayMode === 'empty'" class="lw-c-table__state-row">
          <td :colspan="colCount" class="lw-c-table__state-cell">
            <LwEmpty :title="emptyTitle" :body="emptyBody">
              <template v-if="$slots['empty-illustration']" #illustration>
                <slot name="empty-illustration" />
              </template>
              <template v-if="$slots['empty-action']" #action>
                <slot name="empty-action" />
              </template>
            </LwEmpty>
          </td>
        </tr>

        <template v-if="displayMode === 'rows'">
          <tr
            v-for="(row, index) in visibleRows"
            :key="resolveRowKey(row)"
            class="lw-c-table__row"
            :class="{ 'lw-c-table__row--activatable': isActivatable }"
            :tabindex="isActivatable ? 0 : null"
            :role="isActivatable ? 'button' : null"
            :aria-label="isActivatable ? rowAriaLabel(index) : null"
            @click="handleRowClick($event, row, index)"
            @keydown="handleRowKeydown($event, row, index)"
          >
            <td
              v-for="col in columns"
              :key="col.key"
              class="lw-c-table__cell"
              :class="cellClasses(col)"
            >
              <slot
                :name="'cell-' + col.key"
                :row="row"
                :value="resolveValue(row, col)"
                :index="index"
              >
                {{ resolveValue(row, col) }}
              </slot>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <!--
      Pagination footer (refresh_plan.md 1 "LwTable" + 2.7, paper cut 11:
      "Dispatch paginates at a hardcoded 10; five lists never paginate").
      `pageSize: null` is the component's default and renders none of this;
      a caller opts in per instance instead of the old per="10" that
      cTablePaginated hard-coded for every table that used it
      (audit_web.md 3.9). `:hide-single-page="true"` was the one thing
      vue-paginate got right, so it is kept: a page count of 1 gets no nav.
      Real <button>s via LwButton (paper cut 13: "Dispatch's pagination
      links are Bootstrap blue, the only blue in the product"), so the
      tokened focus ring and hierarchy apply here for free instead of
      reintroducing a sixth link colour.
    -->
    <nav v-if="paginationVisible" class="lw-c-table__pagination" :aria-label="`${caption} pages`">
      <LwButton
        type="button"
        variant="ghost"
        size="sm"
        :disabled="currentPage === 1"
        @click="goToPage(currentPage - 1)"
      >
        Previous
      </LwButton>
      <span class="lw-c-table__pagination-pages">
        <template
          v-for="item in pageWindowItems"
          :key="item.type === 'ellipsis' ? item.key : 'page-' + item.page"
        >
          <span
            v-if="item.type === 'ellipsis'"
            class="lw-c-table__pagination-ellipsis"
            aria-hidden="true"
          >
            &hellip;
          </span>
          <LwButton
            v-else
            type="button"
            :variant="item.page === currentPage ? 'secondary' : 'ghost'"
            size="sm"
            :aria-current="item.page === currentPage ? 'page' : null"
            @click="goToPage(item.page)"
          >
            {{ item.page }}
          </LwButton>
        </template>
      </span>
      <LwButton
        type="button"
        variant="ghost"
        size="sm"
        :disabled="currentPage === pageCount"
        @click="goToPage(currentPage + 1)"
      >
        Next
      </LwButton>
    </nav>

    <!--
      Visually hidden live region announcing the loading state (contract).
      Always present, text only while loading, so a screen reader user gets
      one announcement on the way in and one implicit "gone" when rows
      replace it, rather than depending on a `role="status"` element that
      pops in and out of the DOM.

      Pagination shares the region rather than getting a second one: a
      polite live region only speaks on a text CHANGE, so a "Page 1 of 3"
      value present from first paint announces nothing on mount and reads
      out exactly once per page change, which is the only time it should.
    -->
    <div class="lw-c-table__live-region" aria-live="polite">
      {{ liveRegionMessage }}
    </div>
  </div>
</template>

<script>
import LwButton from './LwButton.vue';
import LwEmpty from './LwEmpty.vue';

const DENSITIES = ['compact', 'comfortable'];
const DIRECTIONS = ['asc', 'desc'];

export default {
  name: 'LwTable',
  components: { LwButton, LwEmpty },
  props: {
    // Each column: { key, label, sortable?, width?, align?, numeric? }.
    // `key` may be a dotted path ('customer.name'). Recognisably the same
    // idea as cTable's column object (key/label/sort/class), narrowed: this
    // component owns sorting UI, it does not accept a sort() function or a
    // value() function on the column, because both of those are how the
    // old table ended up with a mutate-in-place, one-column-at-a-time sort
    // (audit_web.md 3.9).
    columns: {
      type: Array,
      required: true,
    },
    rows: {
      type: Array,
      required: true,
    },
    // A function receives the row. Deliberately never falls back to the
    // v-for index: a keyed list that reorders under a controlled sort would
    // silently reuse the wrong DOM node/focus state if it did.
    rowKey: {
      type: [String, Function],
      default: 'id',
    },
    loading: {
      type: Boolean,
      required: true,
    },
    // Required, no default: the plan's rule is "a missing error prop is a
    // lint failure" (refresh_plan.md 1.2), so the caller must always make a
    // choice instead of omitting the prop the way all fifteen G2 list pages
    // omit a .catch today (audit_web.md 3.8). `null` is that choice's "no
    // error" value and has to type-check cleanly: Vue's type array cannot
    // express "String, Object, or null" (validateProp only skips type
    // checking on a null value when the prop is NOT required, and this one
    // has to be required), so the shape is enforced with a validator
    // instead of `type`.
    error: {
      required: true,
      validator: value => value === null || typeof value === 'string' || typeof value === 'object',
    },
    emptyTitle: {
      type: String,
      required: true,
    },
    emptyBody: {
      type: String,
      required: true,
    },
    // The table's accessible name (a <caption>, not an aria-label on
    // <table>, because a caption is also usable as visible copy when the
    // page wants it, see captionVisible).
    caption: {
      type: String,
      required: true,
    },
    captionVisible: {
      type: Boolean,
      default: false,
    },
    density: {
      type: String,
      default: 'comfortable',
      validator: value => DENSITIES.includes(value),
    },
    sort: {
      type: Object,
      default: null,
    },
    // Required only when rows are activatable (see the isActivatable watcher
    // below); no static Vue validator can express "required conditional on
    // a listener being bound", so that case is enforced at runtime instead.
    rowActivateLabel: {
      type: String,
      default: null,
    },
    // `null` (the default) is byte-for-byte the pre-pagination component:
    // every row renders, no footer, no page state. A caller opts a table
    // into pagination by naming a page size, the way it opts into row
    // activation by binding a listener above; nothing here is on by
    // default (refresh_plan.md 1 "LwTable" + 2.7, paper cut 11).
    pageSize: {
      type: Number,
      default: null,
      validator: v => v === null || (Number.isInteger(v) && v > 0),
    },
    // Server-side pagination (F3, ai_docs/perf/web_memory_investigation.md):
    // both default null, and null means everything above this comment is
    // byte-for-byte the client-paged component. A parent that fetches one
    // page at a time sets BOTH: `totalRows` (how many rows exist on the
    // server, which is what the page count has to be computed from once
    // `rows` is only one page of them) and `page` (which page `rows` is).
    // With `page` set, pagination becomes a controlled prop exactly like
    // `sort` already is: the footer's buttons emit `page-change` and change
    // nothing themselves, because the component cannot turn a page it does
    // not hold -- only the parent's refetch can.
    page: {
      type: Number,
      default: null,
      validator: v => v === null || (Number.isInteger(v) && v > 0),
    },
    totalRows: {
      type: Number,
      default: null,
      validator: v => v === null || (Number.isInteger(v) && v >= 0),
    },
  },
  emits: ['retry', 'sort', 'row-activate', 'page-change'],
  data() {
    return {
      // 1-based, this component's own state when pagination is UNcontrolled
      // (`page: null`, the client-paged default): nothing outside this
      // component reads it. When the `page` prop is set, this field is
      // simply never consulted -- see `currentPage` below.
      internalPage: 1,
    };
  },
  computed: {
    densityClass() {
      return `lw-c-table--${this.density}`;
    },
    colCount() {
      return this.columns.length || 1;
    },
    hasError() {
      // '' is not a message; only a real string or an error-shaped object
      // counts. null/undefined is "no error".
      return this.error !== null && this.error !== undefined && this.error !== '';
    },
    errorMessage() {
      if (!this.hasError) return '';
      if (typeof this.error === 'string') return this.error;
      if (this.error && typeof this.error.message === 'string') return this.error.message;
      return String(this.error);
    },
    // Error wins over loading, which wins over empty, which wins over rows
    // (contract's stated precedence, verbatim).
    displayMode() {
      if (this.hasError) return 'error';
      if (this.loading) return 'loading';
      if (this.rows.length === 0) return 'empty';
      return 'rows';
    },
    ariaBusy() {
      return this.displayMode === 'loading' ? 'true' : null;
    },
    // Loading wins the announcement outright (a caption mid-fetch has
    // nothing else worth saying); pagination only gets a say once rows are
    // showing and there is more than one page to be on, matching
    // `paginationVisible` below so the region never announces a page count
    // the footer itself is not displaying.
    liveRegionMessage() {
      if (this.displayMode === 'loading') return `Loading ${this.caption}`;
      if (this.paginationVisible) return `Page ${this.currentPage} of ${this.pageCount}`;
      return '';
    },
    // A row is only activatable when a parent actually bound the listener
    // (contract: "faking interactivity is worse than not having it"). A
    // click handler with nothing listening would be harmless, but the
    // tabindex/role/aria-label/keyboard path is not, so all of it is gated
    // on the same check. Vue 3 has no `$listeners`; a declared emit's
    // listener shows up as an `onRowActivate` vnode prop instead
    // (`@row-activate` compiles to that), so it is read off the vnode
    // directly.
    isActivatable() {
      const props = this.$.vnode.props;
      return Boolean(props && props.onRowActivate);
    },
    // Controlled when the parent set the `page` prop, uncontrolled
    // otherwise; every template binding and method reads this, never
    // `internalPage` directly, so the two modes cannot drift.
    currentPage() {
      return this.page !== null ? this.page : this.internalPage;
    },
    // `pageSize: null` (the default) is one page by definition: nothing
    // downstream of this computed should ever need to distinguish "not
    // paginating" from "paginating with one page", so both collapse to 1.
    // With `totalRows` set, `rows` is one page and the denominator is the
    // server's count.
    pageCount() {
      if (this.pageSize === null) return 1;
      const count = this.totalRows !== null ? this.totalRows : this.rows.length;
      return Math.max(1, Math.ceil(count / this.pageSize));
    },
    // The rendered slice. Every place that used to read `rows` for what is
    // on screen -- the row v-for, the "row N of M" label, the arrow-key
    // bounds -- reads this instead, so keyboard focus can never walk off
    // the edge of the visible page onto a row that is not in the DOM.
    // Server-paged rows are already one page and are never sliced: slicing
    // page 2's 25 rows by "start at row 25" would render nothing.
    visibleRows() {
      if (this.pageSize === null || this.totalRows !== null) return this.rows;
      const start = (this.currentPage - 1) * this.pageSize;
      return this.rows.slice(start, start + this.pageSize);
    },
    // Matches the old cTablePaginated's `:hide-single-page="true"`: a table
    // with one page of results gets no footer, and the footer never appears
    // outside the 'rows' display mode (contract's precedence again -- a
    // loading, empty or error table has nothing to page through).
    paginationVisible() {
      return this.displayMode === 'rows' && this.pageSize !== null && this.pageCount > 1;
    },
    // The page-button list, windowed above 9 pages. Below that, every page
    // number renders (refresh_plan.md's "keep it simple and honest"); at 9
    // or above, only the first page, the last page, and a sibling either
    // side of the current page render, with a non-interactive ellipsis
    // filling each gap. The ellipsis is never a button: a table with 40
    // pages does not get a working "jump forward 10" control by accident,
    // it gets an honest "there is more here" marker.
    pageWindowItems() {
      const total = this.pageCount;
      if (total <= 9) {
        const items = [];
        for (let page = 1; page <= total; page += 1) items.push({ type: 'page', page });
        return items;
      }
      const current = this.currentPage;
      const left = Math.max(2, current - 1);
      const right = Math.min(total - 1, current + 1);
      const items = [{ type: 'page', page: 1 }];
      if (left > 2) items.push({ type: 'ellipsis', key: 'start-ellipsis' });
      for (let page = left; page <= right; page += 1) items.push({ type: 'page', page });
      if (right < total - 1) items.push({ type: 'ellipsis', key: 'end-ellipsis' });
      items.push({ type: 'page', page: total });
      return items;
    },
  },
  watch: {
    isActivatable: {
      immediate: true,
      handler(activatable) {
        if (activatable && !this.rowActivateLabel) {
          // eslint-disable-next-line no-console -- deliberate contract warning, see LwButton's identical pattern for the primary-button rule
          console.warn(
            'LwTable: rowActivateLabel is required when a row-activate listener is bound.'
          );
        }
      },
    },
    // The old vue-paginate reset to page 1 whenever the underlying list
    // changed identity, which is what makes a re-sort land you at the top
    // of the new order instead of stranding you on, say, "page 4" of a
    // list that only sorted the values within each page. This is a
    // reference-identity watch (Vue's default, not `deep`), so it does not
    // fire on a sort that reorders `rows` in place -- but this component
    // never receives a `rows` prop that was mutated in place; see the
    // "never mutates the rows prop" contract on handleSort below.
    //
    // Controlled mode opts out of both watchers: there, a new `rows` array
    // is the ordinary arrival of the page the parent just fetched (resetting
    // to 1 on it would make page 2 unreachable), and where the current page
    // sits relative to the page count is the parent's decision to make with
    // the total it fetched.
    rows() {
      if (this.page === null) this.internalPage = 1;
    },
    // Catches every other way internalPage could end up past the last page:
    // `pageSize` shrinking, or `rows` shrinking without changing identity
    // (a caller reassigning a shorter array still changes identity and
    // triggers the watcher above, but this is the backstop for pageSize).
    pageCount(newPageCount) {
      if (this.page === null && this.internalPage > newPageCount) {
        this.internalPage = newPageCount;
      }
    },
  },
  methods: {
    // Dotted-path resolution ('customer.name'); returns undefined rather
    // than throwing when a segment is missing, unlike cTableRow's
    // getNestedKeyValue, because a table cell rendering blank is recoverable
    // and a thrown render error is not.
    resolveValue(row, col) {
      const path = String(col.key).split('.');
      return path.reduce((acc, segment) => (acc == null ? acc : acc[segment]), row);
    },
    resolveRowKey(row) {
      return typeof this.rowKey === 'function' ? this.rowKey(row) : row[this.rowKey];
    },
    headerCellClasses(col) {
      return {
        'lw-c-table__header-cell--align-right': col.align === 'right',
      };
    },
    cellClasses(col) {
      return {
        'lw-c-table__cell--align-right': col.align === 'right',
        'lw-c-table__cell--numeric': col.numeric === true,
      };
    },
    colStyle(col) {
      return col.width ? { width: col.width } : {};
    },
    ariaSortFor(col) {
      if (!col.sortable) return null;
      if (this.sort && this.sort.key === col.key) {
        return this.sort.direction === 'asc' ? 'ascending' : 'descending';
      }
      return 'none';
    },
    sortIconClasses(col) {
      const state = this.ariaSortFor(col);
      return {
        'lw-c-table__sort-icon--ascending': state === 'ascending',
        'lw-c-table__sort-icon--descending': state === 'descending',
      };
    },
    // Controlled component: this never sorts `rows` itself and never
    // mutates the array it was given (contract). It only ever tells the
    // parent what the next state should be; ascending first, then toggling,
    // which is what replaces the old $once('sort') unsort dance that could
    // only track one sorted column at a time (audit_web.md 3.9).
    handleSort(col) {
      if (!col.sortable) return;
      let direction = DIRECTIONS[0];
      if (this.sort && this.sort.key === col.key && this.sort.direction === 'asc') {
        direction = 'desc';
      }
      this.$emit('sort', { key: col.key, direction });
    },
    // Denominator is `visibleRows`, not `rows`: on page 2 of 3, row 1 of the
    // page has to announce "row 1 of 2" (the size of that page), not "row 1
    // of 5" (the whole list), or the announcement would describe a table
    // that is not the one on screen.
    rowAriaLabel(index) {
      return `${this.rowActivateLabel}, row ${index + 1} of ${this.visibleRows.length}`;
    },
    // A control inside a cell owns its own click. The prototype's stops table
    // puts a "Confirm delivery" button in the custody cell of an activatable
    // row (direction-1.html), and without this check pressing it would both
    // confirm the delivery and navigate away from the page that was about to
    // show the result. The same reasoning covers a link, an input, or a menu
    // trigger dropped into a cell slot by a caller in phase 2.
    isInteractiveTarget(event) {
      const target = event.target;
      if (!target || typeof target.closest !== 'function') return false;
      const control = target.closest('a[href], button, input, select, textarea, label');
      return Boolean(control) && control !== event.currentTarget;
    },
    // `index` here is the index within `visibleRows` -- the position on the
    // rendered page, not the position in the caller's full `rows` array --
    // because that is what the v-for above actually iterates. The row
    // OBJECT is unaffected by pagination either way: page 2's row 0 is
    // still that page's real row, just addressed relative to what is on
    // screen rather than to the whole list.
    handleRowClick(event, row, index) {
      if (!this.isActivatable) return;
      if (this.isInteractiveTarget(event)) return;
      this.$emit('row-activate', row, index);
    },
    handleRowKeydown(event, row, index) {
      if (!this.isActivatable) return;
      // A keypress that lands on a control inside the cell belongs to that
      // control: Enter on a cell's button must press the button, and the arrow
      // keys inside a cell's select must change the selection, not move row
      // focus.
      if (this.isInteractiveTarget(event)) return;
      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          this.$emit('row-activate', row, index);
          break;
        case ' ':
        case 'Spacebar':
          // Space's native behaviour is to scroll the page; that only
          // happens because a <tr role="button"> is not a real button, so
          // it has to be suppressed by hand (contract).
          event.preventDefault();
          this.$emit('row-activate', row, index);
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.focusRow(index + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.focusRow(index - 1);
          break;
        case 'Home':
          event.preventDefault();
          this.focusRow(0);
          break;
        case 'End':
          event.preventDefault();
          this.focusRow(this.visibleRows.length - 1);
          break;
        default:
          break;
      }
    },
    // Focus by calling focus() directly on the sibling row element (contract
    // allows either this or roving tabindex; every activatable row already
    // carries tabindex="0", so a direct focus() call is the smaller of the
    // two to reason about).
    //
    // The row elements are read out of the DOM rather than from a `ref` on
    // the v-for, which is what this did under Vue 2. Vue 3 fills a v-for ref
    // array in MOUNT order and never reorders it, so after a controlled
    // re-sort (same rowKeys, new order, so every <tr> is moved rather than
    // recreated) `$refs.rowEl` still holds the PRE-sort order while `index`
    // here is a position in the post-sort render -- ArrowDown would move
    // focus to whatever row used to be below. querySelectorAll is in
    // document order by definition, which is what "the next row down" means.
    // The `:scope > table > tbody` prefix keeps this instance's rows from
    // matching a nested LwTable's rows inside a cell slot, which the old
    // per-instance `$refs` lookup gave for free.
    focusRow(index) {
      const rowEls = this.$el.querySelectorAll(':scope > table > tbody > .lw-c-table__row');
      if (index < 0 || index >= rowEls.length) return;
      const el = rowEls[index];
      if (el && typeof el.focus === 'function') el.focus();
    },
    // Shared by Previous, Next and every numbered button. Out-of-range and
    // same-page requests are no-ops rather than clamped, so a disabled
    // Previous/Next click (which should not reach here at all, see the
    // `disabled` bindings in the template) can never walk currentPage past
    // either end even if it did. Controlled mode announces instead of
    // acting, the exact shape handleSort already has: the parent owns the
    // page, so the button's job ends at saying which page was asked for.
    goToPage(page) {
      if (page < 1 || page > this.pageCount || page === this.currentPage) return;
      if (this.page !== null) {
        this.$emit('page-change', page);
        return;
      }
      this.internalPage = page;
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../../sass/mixins.generated';

.lw-c-table {
  // Elev-0 per the contract: flat, border only. A table row is not a card,
  // so no shadow token is applied even though --lw-elev-0-shadow exists
  // (it resolves to `none` today, but the point is this reads the token
  // rather than assuming that value).
  background: var(--lw-elev-0-surface);
  border: 1px solid var(--lw-elev-0-line);
  box-shadow: var(--lw-elev-0-shadow);
  border-radius: var(--lw-radius-md);
  // Clips the header's square corners to the wrapper's rounded ones.
  overflow: hidden;
}

// On a phone a board of eight columns cannot fit, and it must not widen the
// page to try (app.scss, the phone block). It scrolls sideways inside its own
// box instead: the first column stays in reach with a swipe, and the page
// around it stays the width of the screen.
@media (max-width: 768px) {
  .lw-c-table {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
}

.lw-c-table__table {
  width: 100%;
  border-collapse: collapse;
}

// Standard visually-hidden clip: off-flow, zero-area, still read by a
// screen reader. Shared shape with LwButton's __sr-only and LwEmpty has no
// equivalent because it never needs one; boilerplate geometry, not a
// stylistic choice, so no token applies.
.lw-c-table__caption,
.lw-c-table__live-region {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.lw-c-table__caption {
  caption-side: top;
  text-align: left;
}

.lw-c-table__caption--visible {
  position: static;
  width: auto;
  height: auto;
  margin: 0;
  padding: var(--lw-space-4) var(--lw-space-4) var(--lw-space-2);
  clip: auto;
  overflow: visible;
  white-space: normal;
  font: var(--lw-type-subhead);
  color: var(--lw-fg-primary);
}

// ===== Header: plate face, caps, tracked, 2px navy rule (direction-1.html
// `thead th`) =====
.lw-c-table__header-cell {
  text-align: left;
  vertical-align: bottom;
  font: var(--lw-type-label);
  letter-spacing: var(--lw-type-label-tracking);
  text-transform: var(--lw-type-label-transform);
  color: var(--lw-fg-secondary);
  // 2px header rule: the prototype's exact thickness (direction-1.html
  // `thead th { border-bottom: 2px solid var(--ink-900) }`); no token in the
  // scale represents "the header's own rule width", same exception class as
  // a hairline (hard rule 2).
  border-bottom: 2px solid var(--lw-navy-900);
}

.lw-c-table__header-cell--align-right {
  text-align: right;
}

.lw-c-table__sort-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--lw-space-1);
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
  padding: 0;

  @include lw-focus-visible;
}

.lw-c-table__sort-icon {
  display: inline-block;
  // The glyph itself needs no colour rule: currentColor tracks the header
  // text colour so the icon never introduces a second colour decision.
  opacity: 0.55;

  &::after {
    content: '\2195'; // up-down arrow: idle/unsorted, matches aria-sort="none"
  }
}

.lw-c-table__sort-icon--ascending {
  opacity: 1;

  &::after {
    content: '\2191'; // up arrow
  }
}

.lw-c-table__sort-icon--descending {
  opacity: 1;

  &::after {
    content: '\2193'; // down arrow
  }
}

// ===== Body =====
.lw-c-table__cell {
  vertical-align: top;
  text-align: left;
  // 1px hairline row separator (direction-1.html `tbody td`); same
  // no-token exception as the header rule above.
  border-bottom: 1px solid var(--lw-line-hairline);
  font: var(--lw-type-body-sm);
  color: var(--lw-fg-primary);
}

.lw-c-table__cell--align-right {
  text-align: right;
}

// Tabular numerals for money and times (refresh_plan.md 2.3), read from the
// data role token rather than hardcoding font-variant-numeric here, so a
// future change to which numerals are tabular happens once, in the token
// file.
.lw-c-table__cell--numeric {
  font-variant-numeric: var(--lw-type-data-numeric);
}

.lw-c-table__row:last-child .lw-c-table__cell {
  border-bottom: 0;
}

.lw-c-table__row--activatable {
  cursor: pointer;

  &:hover {
    background: var(--lw-bg-inset);
  }

  @include lw-focus-visible;

  // The mixin's ring sits outside the element by --lw-focus-ring-offset.
  // On a full-width row that outer ring gets clipped by the next row's
  // hairline, so the offset is flipped to negative (contract: "outline-
  // offset may be negative so the ring stays inside the row"). Still a pure
  // token read, just negated, so this is not a raw-value exception.
  &:focus-visible {
    outline-offset: calc(var(--lw-focus-ring-offset) * -1);
  }
}

// ===== Density: padding and row height read from the token pair, never a
// literal (contract). This is the single place that corrects both of the
// audit's density extremes: Dispatch's ~49px rows (4.3, paper cut 15) and
// the Stops table's zero padding (4.3, paper cut 4).
.lw-c-table--compact .lw-c-table__cell,
.lw-c-table--compact .lw-c-table__header-cell {
  padding: var(--lw-density-row-compact-padding);
}

.lw-c-table--compact .lw-c-table__row {
  height: var(--lw-density-row-compact-height);
}

.lw-c-table--comfortable .lw-c-table__cell,
.lw-c-table--comfortable .lw-c-table__header-cell {
  padding: var(--lw-density-row-comfortable-padding);
}

.lw-c-table--comfortable .lw-c-table__row {
  height: var(--lw-density-row-comfortable-height);
}

// ===== State rows (error / empty) span every column and carry no cell
// padding of their own; LwEmpty and the error block bring their own. =====
.lw-c-table__state-cell {
  padding: 0;
}

.lw-c-table__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--lw-space-3);
  text-align: center;
  padding: var(--lw-space-8) var(--lw-space-6);
  color: var(--lw-fg-primary);
}

.lw-c-table__error-message {
  margin: 0;
  font: var(--lw-type-body);
}

// ===== Loading skeleton =====
.lw-c-table__skeleton-bar {
  display: block;
  width: 60%;
  // Relative to the cell's own font size rather than a fixed px, so it
  // scales with density's type without needing a size token of its own.
  height: 0.9em;
  border-radius: var(--lw-radius-xs);
  background: linear-gradient(
    90deg,
    var(--lw-bg-inset) 25%,
    var(--lw-elev-1-surface) 37%,
    var(--lw-bg-inset) 63%
  );
  background-size: 400% 100%;
  // Duration comes from the motion token (scaled up, since the token scale
  // tops out at "slow" = 280ms and a shimmer sweep needs to be much slower
  // than that to read as calm rather than jittery), so the token layer's
  // prefers-reduced-motion block, which zeroes --lw-motion-slow, silences
  // this animation for free (contract).
  animation: lw-c-table-shimmer calc(var(--lw-motion-slow) * 6) var(--lw-motion-ease) infinite;
}

@keyframes lw-c-table-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

// ===== Pagination (paper cut 11 + paper cut 13) =====
// A hairline top rule separates the footer from the last row, matching the
// header's own rule rather than inventing a new divider treatment; no
// shadow or extra surface, this is still the table's own elev-0 plate.
.lw-c-table__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--lw-space-2);
  padding: var(--lw-space-3) var(--lw-space-4);
  border-top: 1px solid var(--lw-line-hairline);
}

.lw-c-table__pagination-pages {
  display: flex;
  align-items: center;
  gap: var(--lw-space-1);
}

.lw-c-table__pagination-ellipsis {
  // Matches the numbered buttons' own horizontal rhythm so the row of
  // controls does not visibly narrow where a gap is folded away, without
  // making the marker itself a target: no padding-y, no hover state, no
  // button underneath it (contract: never a clickable ellipsis).
  padding: 0 var(--lw-space-2);
  color: var(--lw-fg-secondary);
  font: var(--lw-type-body-sm);
}
</style>
